import React, { useState } from 'react';
import { CheckCircle, AlertTriangle, Target, Eye, EyeOff, Sparkles } from 'lucide-react';
import { CISA_FUNCTIONS, CISA_PILLARS, MATURITY_CRITERIA } from '../../../config/cisaMaturityModel';
import { getDiagramTemplate } from '../data/maturityDiagramTemplates';
import JourneyDiagramCanvas from './JourneyDiagramCanvas';

/**
 * Maturity level info for display
 */
const MATURITY_LEVEL_INFO = {
  TRADITIONAL: { label: 'Traditional', color: 'var(--zt-traditional, #6b7280)', shortLabel: 'T' },
  INITIAL: { label: 'Initial', color: 'var(--zt-initial, #eab308)', shortLabel: 'I' },
  ADVANCED: { label: 'Advanced', color: 'var(--zt-advanced, #3b82f6)', shortLabel: 'A' },
  OPTIMAL: { label: 'Optimal', color: 'var(--zt-optimal, #10b981)', shortLabel: 'O' }
};

/**
 * MaturityLevelDiagram Component
 *
 * Displays a full architecture diagram for a specific maturity level.
 * Includes educational sidebar with capabilities and risks.
 */
function MaturityLevelDiagram({
  functionCode,
  level,
  currentLevel,
  targetLevel,
  onBack,
  onStartAssessment
}) {
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [highlightNew, setHighlightNew] = useState(true);

  // Get function and pillar info
  const pillarCode = Object.keys(CISA_FUNCTIONS).find(
    p => CISA_FUNCTIONS[p].some(f => f.code === functionCode)
  );
  const pillar = CISA_PILLARS.find(p => p.code === pillarCode);
  const fn = CISA_FUNCTIONS[pillarCode]?.find(f => f.code === functionCode);
  const template = getDiagramTemplate(functionCode, level);
  const levelInfo = MATURITY_LEVEL_INFO[level];
  const criteria = MATURITY_CRITERIA[functionCode]?.[level];

  const isCurrent = level === currentLevel;
  const isTarget = level === targetLevel;

  if (!template) {
    return (
      <div className="ztj-level-view">
        <div className="ztj-empty-state">
          <AlertTriangle size={48} />
          <p>No diagram template available for {fn?.name || functionCode} at {levelInfo?.label || level} level</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ztj-level-view">
      {/* Header */}
      <div className="ztj-level-header">
        <div
          className="ztj-level-badge-large"
          style={{ background: levelInfo.color }}
        >
          {levelInfo.shortLabel}
        </div>

        <div className="ztj-level-info">
          <h2>
            {template.name}
            {isCurrent && (
              <span style={{ marginLeft: 8, fontSize: '0.75rem', color: 'var(--zt-initial)', fontWeight: 500 }}>
                (Current)
              </span>
            )}
            {isTarget && !isCurrent && (
              <span style={{ marginLeft: 8, fontSize: '0.75rem', color: 'var(--color-primary)', fontWeight: 500 }}>
                (Target)
              </span>
            )}
          </h2>
          <p>{template.description}</p>
        </div>

        <div className="ztj-level-actions">
          {!isCurrent && (
            <button
              type="button"
              className="ztj-action-btn ztj-action-btn--primary"
              onClick={() => onStartAssessment('CURRENT')}
            >
              <Target size={16} />
              Set as Current
            </button>
          )}
        </div>
      </div>

      {/* Diagram container */}
      <div className="ztj-diagram-container">
        <div className="ztj-diagram-toolbar">
          <div className="ztj-diagram-toggles">
            <button
              type="button"
              className={`ztj-diagram-toggle ${showAnnotations ? 'ztj-diagram-toggle--active' : ''}`}
              onClick={() => setShowAnnotations(!showAnnotations)}
            >
              {showAnnotations ? <Eye size={14} /> : <EyeOff size={14} />}
              Annotations
            </button>
            {level !== 'TRADITIONAL' && (
              <button
                type="button"
                className={`ztj-diagram-toggle ${highlightNew ? 'ztj-diagram-toggle--active' : ''}`}
                onClick={() => setHighlightNew(!highlightNew)}
              >
                <Sparkles size={14} />
                Highlight New
              </button>
            )}
          </div>

          <div className="ztj-diagram-legend">
            <span className="ztj-legend-item">
              <span className="ztj-legend-dot ztj-legend-dot--existing" />
              Existing
            </span>
            <span className="ztj-legend-item">
              <span className="ztj-legend-dot ztj-legend-dot--new" />
              New at this level
            </span>
          </div>
        </div>

        <div className="ztj-canvas-wrapper">
          <JourneyDiagramCanvas
            template={template}
            showAnnotations={showAnnotations}
            highlightNew={highlightNew}
          />
        </div>
      </div>

      {/* Side panels */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4, 16px)' }}>
        {/* Key Capabilities */}
        <div className="ztj-side-panel">
          <h3>
            <CheckCircle size={18} style={{ color: 'var(--color-success)' }} />
            Key Capabilities
          </h3>
          <ul className="ztj-capabilities-list">
            {template.keyCapabilities?.map((cap, idx) => (
              <li key={idx} className="ztj-capability-item">
                <CheckCircle size={14} />
                {cap}
              </li>
            ))}
          </ul>
        </div>

        {/* Risks / Considerations */}
        <div className="ztj-side-panel">
          <h3>
            <AlertTriangle size={18} style={{ color: 'var(--color-warning)' }} />
            Risks & Considerations
          </h3>
          <ul className="ztj-risks-list">
            {template.risks?.map((risk, idx) => (
              <li key={idx} className="ztj-risk-item">
                <AlertTriangle size={14} />
                {risk}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* CISA Criteria */}
      {criteria && (
        <div className="ztj-side-panel">
          <h3>CISA Maturity Criteria</h3>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)', lineHeight: 1.6, margin: 0 }}>
            {criteria}
          </p>
        </div>
      )}
    </div>
  );
}

export default MaturityLevelDiagram;
